// @react
import React, { ReactElement, useState } from 'react'
// @types
import { GetUserQuery, GetUserQueryResult } from 'lib/graphql/graphqlTypes'
// @graphQL
// @libraries / common
import { makeStyles } from '@material-ui/core/styles'
// @components
import Spacer from '@bit/atd.web.spacer'
import Box from '@bit/atd.web.box'
// @module components
import EditAction from 'modules/profile/components/EditAction'
import EditingAction from 'modules/profile/components/EditingAction'
import EmailUpdateOptionsFields from 'modules/common/forms/fields/communication/EmailUpdateOptions'
import FewerEmailOptionsFields from 'modules/common/forms/fields/communication/FewerEmailOptions'
import PostalOptionFields from 'modules/common/forms/fields/communication/PostalOptions'
import useFormWrapper from 'modules/common/forms/hooks/useCommunicationFormWrapper'
import { CommunicationFormValues } from 'modules/common/forms/types/communication'
import PromotionalOptionsFields from 'modules/common/forms/fields/communication/PromitionalOptions'
import { ApolloQueryResult } from '@apollo/client'
import strings from 'lib/constants/strings'

const useStyles = makeStyles(() => ({
  boxRemoveBottomStyling: {
    boxShadow: 'none',
    borderBottom: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  boxRemoveTopBorderRadius: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  boxBody: {
    padding: 0,
  },
}))

type SelfProps = {
  data: GetUserQueryResult['data']
  refetch: () => Promise<ApolloQueryResult<GetUserQuery>>
}
type PropsType = SelfProps

const PreferencesForm = ({ data: userData }: PropsType): ReactElement => {
  /****************************************
   * CONST
   *****************************************/

  /****************************************
   * STATE
   *****************************************/

  const [editing, setEditing] = useState(false)
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)

  /****************************
   *  HOOKS
   *****************************/
  const {
    formMethods,
    control,
    defaultValues,
    userCommunicationPreferencesUpdateHandler,
  } = useFormWrapper({
    data: userData,
  })
  const classes = useStyles()

  /****************************************
   * GQL HOOKS
   *****************************************/

  /****************************************
   * FUNCTIONS
   *****************************************/

  const onEditHandler = () => {
    setEditing(true)
  }

  /****************************************
   * RENDER
   *****************************************/

  const onCancelHandler = () => {
    setEditing(false)
    formMethods.clearErrors()
    formMethods.reset()
  }

  const onSubmitHandler = async (data: CommunicationFormValues) => {
    setIsSubmitting(true)
    const res = await userCommunicationPreferencesUpdateHandler(data)
    if (res) {
      setEditing(false)
    }
  }

  return (
    <>
      <form>
        <Box
          classNames={{
            root: classes.boxRemoveBottomStyling,
            body: classes.boxBody,
          }}
          header={{
            title: !editing
              ? `${strings.PROFILE.COMMUNICATION.OPTION_CHOOSE_EMAIL_UPDATES}:`
              : strings.PROFILE.COMMUNICATION.EDIT_NOTIFICATIONS,
            actions: (
              <>
                <EditAction
                  disabled={editing}
                  onClick={() => onEditHandler()}
                />
                {editing && (
                  <EditingAction
                    bottom
                    isSubmitting={isMutationSubmitting}
                    onCancel={() => onCancelHandler()}
                    onSubmit={formMethods.handleSubmit((data: any) =>
                      onSubmitHandler(data),
                    )}
                  />
                )}
              </>
            ),
          }}
        >
          <EmailUpdateOptionsFields
            formMethods={formMethods}
            control={control}
            data={userData}
            isEditing={editing}
            watchFields={(defaultValues && Object.keys(defaultValues)) || []}
          />
        </Box>

        <Box
          classNames={{
            root: classes.boxRemoveTopBorderRadius,
            body: classes.boxBody,
          }}
        >
          <PromotionalOptionsFields
            formMethods={formMethods}
            control={control}
            isEditing={editing}
            watchFields={(defaultValues && Object.keys(defaultValues)) || []}
          />
        </Box>

        <Box
          classNames={{
            root: classes.boxRemoveTopBorderRadius,
            body: classes.boxBody,
          }}
          header={{
            title: `${strings.PROFILE.COMMUNICATION.FEWER_EMAILS}:`,
          }}
        >
          <FewerEmailOptionsFields
            formMethods={formMethods}
            control={control}
            isEditing={editing}
            watchFields={(defaultValues && Object.keys(defaultValues)) || []}
          />
        </Box>

        {
          <>
            <Spacer size={24} />
            <Box
              classNames={{
                body: classes.boxBody,
              }}
              header={{
                title: `${strings.PROFILE.COMMUNICATION.SELECT_POSTAL_MAIL}:`,
              }}
            >
              <PostalOptionFields
                formMethods={formMethods}
                control={control}
                isEditing={editing}
              />
            </Box>
          </>
        }
      </form>
    </>
  )
}

export default PreferencesForm
